/**
 * The few things every page states the same way. A date that reads one way on
 * the dashboard and another in the billing table is two facts to the reader.
 */
export function isoDay(value: number | string | null): string {
  if (value === null) return '—';
  const date = typeof value === 'number' ? new Date(value) : new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toISOString().slice(0, 10);
}

/** A day in prose, « Oct 18, 2027 » — how the billing page says a date in a sentence (redesign §43). */
export function proseDay(value: number | string | null): string {
  if (value === null) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export function monthYear(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function money(cents: number, currency: string): string {
  const symbol = currency.toLowerCase() === 'eur' ? '€' : '$';
  return `${symbol}${(cents / 100).toFixed(2)}`;
}

/** What the four access states are called where a customer reads them. */
export const ACCESS_LABEL: Record<string, string> = {
  none: 'No licence yet',
  active: 'Active',
  grace: 'In grace',
  lapsed: 'Lapsed',
  revoked: 'Revoked'
};

/**
 * How long ago, in the words somebody would use.
 *
 * A list of workspaces is read to find out which one was touched, and an ISO
 * stamp makes that a subtraction the reader has to do. Anything inside a week
 * is said in relative terms, and past that a date is clearer than "13 days
 * ago" — nobody counts in thirteens.
 */
export function since(value: string | null): string {
  if (!value) return '—';
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return '—';
  const minutes = Math.round((Date.now() - at.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  const sameYear = at.getUTCFullYear() === new Date().getUTCFullYear();
  return at.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC'
  });
}

export function initialsOf(email: string, name: string | null): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    return (parts[0]?.[0] ?? '').concat(parts[1]?.[0] ?? '').toUpperCase() || email.slice(0, 2).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

/** Verbs whose -ing form the plain rule gets wrong, or that are not verbs at all. */
const GERUNDS: Record<string, string> = {
  'sign in': 'Signing in',
  cut: 'Cutting',
  cancel: 'Cancelling',
  new: 'Creating',
  set: 'Setting'
};

/**
 * A button's verb while it runs (redesign, rule 6): « Remove it » reads
 * « Removing », with no « … » — the button says what it is doing, not that
 * something is happening. Only the first word turns; the rest is dropped.
 */
export function gerund(label: string): string {
  const whole = label.trim().toLowerCase();
  if (GERUNDS[whole]) return GERUNDS[whole];
  const word = label.trim().split(/\s+/)[0] ?? '';
  const lower = word.toLowerCase();
  if (GERUNDS[lower]) return GERUNDS[lower];
  const stem = lower.endsWith('e') && !lower.endsWith('ee') ? lower.slice(0, -1) : lower;
  return `${stem.charAt(0).toUpperCase()}${stem.slice(1)}ing`;
}

/** A stamp as the API returns it — ISO, epoch ms, or D1's « 2026-10-16 14:32:05 », which is UTC. */
export function parseStamp(value: number | string | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date =
    typeof value === 'number' ? new Date(value) : new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * A date in a table (rule 29): relative under seven days, « 21 hours ago »,
 * and the ISO day after, « 2026-08-02 », which lines up down a column. A date
 * still to come is always the day.
 */
export function tableWhen(value: number | string | null | undefined, now: number = Date.now()): string {
  const date = parseStamp(value);
  if (!date) return '—';
  const minutes = Math.floor((now - date.getTime()) / 60000);
  if (minutes < 0 || minutes >= 7 * 24 * 60) return date.toISOString().slice(0, 10);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

/** The exact moment behind a date cell, for its tooltip (rule 29): « Oct 16, 2026, 14:32 UTC ». */
export function exactWhen(value: number | string | null | undefined): string {
  const date = parseStamp(value);
  if (!date) return '';
  return `${proseDay(date.getTime())}, ${date.toISOString().slice(11, 16)} UTC`;
}
